import { useContext, useEffect, useMemo, useRef, useState, type RefObject } from 'react';

import { BreakpointContext } from './breakpoint-context';
import { toBreakpointState, type BreakpointState } from './breakpoints';

/**
 * Meet de breedte van een element - niet van het venster - en vertaalt die naar
 * een responsief niveau. Een scherm kan in een smalle kolom staan terwijl het
 * venster breed is; dan telt de kolom.
 */
export function useElementBreakpoint<T extends HTMLElement>(): {
    ref: RefObject<T | null>;
    state: BreakpointState;
} {
    const ref = useRef<T>(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const element = ref.current;

        if (!element) {
            return;
        }

        const measure = () => {
            setWidth((current) => {
                const next = element.getBoundingClientRect().width;

                return Math.round(current) === Math.round(next) ? current : next;
            });
        };

        if (typeof ResizeObserver === 'undefined') {
            queueMicrotask(measure);
            window.addEventListener('resize', measure);

            return () => window.removeEventListener('resize', measure);
        }

        const observer = new ResizeObserver(measure);
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    return { ref, state: useMemo(() => toBreakpointState(width), [width]) };
}

/**
 * Leest het niveau van de dichtstbijzijnde `BreakpointProvider`. Zonder
 * provider valt de hook terug op de vensterbreedte, zodat losse componenten en
 * tests altijd een bruikbare waarde krijgen.
 */
export function useBreakpoint(): BreakpointState {
    const fromContext = useContext(BreakpointContext);
    const windowWidth = useWindowWidth(fromContext === null);
    const fallback = useMemo(() => toBreakpointState(windowWidth), [windowWidth]);

    return fromContext ?? fallback;
}

function useWindowWidth(enabled: boolean): number {
    const [width, setWidth] = useState(() =>
        typeof window === 'undefined' ? 0 : window.innerWidth,
    );

    useEffect(() => {
        if (!enabled || typeof window === 'undefined') {
            return;
        }

        const handleResize = () => setWidth(window.innerWidth);

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [enabled]);

    return width;
}
